<template>
	<div class="field">
		<Input
			className="field__input"
			:label="label"
			:placeholder="placeholder"
			:helpText="helpText"
			ariaLabel="Aria Label"
			:required="required"
			:showFieldTextError="showFieldTextError"
			:inputType="inputType"
			:showIconCleanOption="showCloseOption"
			@keydown.enter="enterKeyDown"
			@enterKeyUp="enterKeyUp"
			@keydown.down="downKeyPressed"
			@keydown.up="upKeyPressed"
			@click="toggleSuggestions"
			:value="value"
			@input="updateValue"
		/>
		<DropdownList
			ref="suggestionsList"
			:autosuggestion="false"
			:id="`${uid}_ddl`"
			:isOpen="open"
			:options="matches"
			:value="dataValue"
			v-show="hasAutosuggestion && matches.length"
			@change="closeSuggestion"
		/>
	</div>
</template>

<script>
import Input from '@/components/forms/input/input';
import DropdownList from '@/components/forms/dropdown/dropdown-list';
import DocumentClickedMixin from '@/mixins/document-click';
import {
	required,
	minLength,
	maxLength,
	minValue,
	maxValue
} from 'vuelidate/lib/validators';
import _ from 'lodash';

export default {
	name: 'Autosuggestion',
	mixins: [DocumentClickedMixin('closeSuggestion', 'hasDropdownList')],
	components: {
		Input,
		DropdownList
	},
	props: {
		label: {
			type: String,
			default: ''
		},
		helpText: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: ''
		},
		inputType: {
			type: String,
			default: ''
		},
		required: {
			type: Boolean,
			default: false
		},
		value: {
			type: [String, Number],
			default: ''
		},
		maxLength: {
			type: Number,
			default: 100
		},
		minLength: {
			type: Number,
			default: 0
		},
		max: {
			type: Number,
			default: Number.MAX_VALUE
		},
		min: {
			type: Number,
			default: 0
		},
		suggestionsList: {
			type: Array,
			default: () => []
		},
		matchingValueMustBeExisting: {
			type: Boolean,
			default: false
		},
		timeout: {
			type: Number,
			default: 0
		},
		errorText: {
			type: String,
			default: ''
		},
		minValue: {
			type: Number
		},
		maxValue: {
			type: Number
		},
		showFieldTextError: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			currentItem: -1,
			matches: [],
			open: false,
			dataValue: ''
		};
	},
	computed: {
		hasDropdownList() {
			return this.hasAutosuggestion;
		},
		hasAutosuggestion() {
			return this.suggestionsList.length > 0;
		},
		showCloseOption() {
			return !!this.dataValue;
		},
		error() {
			if (_.get(this.$v, 'dataValue.$anyError', false)) {
				const value = this.$v.dataValue;
				let msg = '';

				if (value.required === false) {
					msg = 'is required';
				} else if (value.minLength === false) {
					msg = `should have at least ${this.minLength}`;
				} else if (value.maxLength === false) {
					msg = `should have no more than ${this.maxLength}`;
				} else if (value.minValue === false) {
					msg = `should have at least ${this.minValue}`;
				} else if (value.maxValue === false) {
					msg = `should have no more than ${this.maxValue}`;
				}

				return `${this.label || 'Autosuggestion'} ${msg}`;
			}

			return '';
		},
		validations() {
			let result = {};
			if (this.required) {
				result.dataValue = {
					required
				};
			}
			if (this.minLength) {
				result.dataValue = {
					...result.dataValue,
					minLength: minLength(this.minLength)
				};
			}
			if (this.maxLength) {
				result.dataValue = {
					...result.dataValue,
					maxLength: maxLength(this.maxLength)
				};
			}
			if (this.minValue || this.minValue === 0) {
				result.dataValue = {
					...result.dataValue,
					minValue: minValue(this.minValue)
				};
			}
			if (this.maxValue) {
				result.dataValue = {
					...result.dataValue,
					maxValue: maxValue(this.maxValue)
				};
			}

			return { ...result };
		}
	},
	methods: {
		matchingValues(val) {
			const mapping = items =>
				items.map(item => {
					return {
						text: item.toString(),
						value: item
					};
				});
			if (val) {
				const internalMatches = this.suggestionsList.filter(x => {
					return x.toLowerCase().includes(val.toLowerCase());
				});
				const matchesLength = internalMatches.length;

				if (matchesLength == 1 && internalMatches[0] === val) {
					this.open = false;
				}

				if (matchesLength) {
					this.matches = mapping(internalMatches);
					return;
				}
				//no results
				this.matches = [];
				return;
			}
			this.matches = mapping(this.suggestionsList);
		},
		toggleSuggestions() {
			if (this.hasAutosuggestion) {
				this.open = !this.open;
				if (this.open) {
					this.matchingValues(
						(this.dataValue || '').toString().trim()
					);
				}
			} else {
				this.emitEvent(true, 'searchTextClicked');
			}
		},
		closeSuggestion(val) {
			if (val) this.emitEvent(val);
			this.open = false;
		},
		downKeyPressed() {
			if (this.hasAutosuggestion) {
				this.open = true;
				if (this.currentItem < this.matches.length - 1)
					this.currentItem++;
			}
		},
		enterKeyDown() {
			if (this.hasAutosuggestion) {
				const obj = this.matches[this.currentItem];
				if (JSON.stringify(obj) !== '{}') {
					this.closeSuggestion(obj.value);
				}
			}
		},
		enterKeyUp(e) {
			this.$emit('enterKeyUp', e);
		},
		upKeyPressed() {
			if (this.hasAutosuggestion) {
				if (this.currentItem > 0) this.currentItem--;
			}
		},
		updateValue(val) {
			if (this.hasAutosuggestion) {
				this.open = true;
				this.currentItem = -1;
			}
			this.emitEvent(val);
		},
		emitEvent(value, eventName) {
			eventName = eventName || 'input';
			if (
				this.inputType.toLowerCase() === 'number' &&
				value &&
				eventName === 'input'
			) {
				value = Number(value);
			}
			this.$emit(eventName, value);
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(val) {
				this.dataValue = val;
			}
		},
		dataValue: {
			handler(val) {
				this.matchingValues(val);
				if (val !== this.value) {
					this.emitEvent(val);
				}
			}
		},
		matches: {
			handler(val) {
				if (val.length > 1) {
					this.open = true;
				} else {
					this.open = false;
				}
			}
		},
		error: {
			immediate: true,
			handler(val) {
				this.emitEvent({ [this.name]: !!val }, 'hasError');
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
	* Field Input styles
	* Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.field {
	position: relative;

	&__label {
		display: block;
		font-size: $label-font-size;
		font-weight: $label-font-weight;
		color: inherit;
		margin-bottom: $gutter-sm;
	}

	&__input-container {
		position: relative;
		@include textfield;
		display: flex;

		&:focus-within {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}
	}

	&__input {
		appearance: none;
		outline: none;
		border: none;
		@include input-height;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		padding: $input-padding-y $input-padding-x;
		width: 100%;
		background-color: transparent;
	}

	&__trailing-container {
		display: flex;
		align-items: center;
		font-size: $input-font-size + 2;

		.field__trailing-item.has-spinner {
			.btn--icon-only {
				min-width: auto;
				margin-right: $gutter-sm;
			}
		}
	}

	&__icon {
		padding-right: $input-padding-x;
	}

	&__icon--error {
		display: none;
	}
}

/*------------------------------------*\
  #FIELD HELP TEXT
\*------------------------------------*/

.field__text {
	font-size: $label-font-size - 2;
	margin-top: $gutter-sm;
	color: $neutral-2;

	&--error {
		display: none;
	}
}

/*------------------------------------*\
  #ERROR
\*------------------------------------*/

.has-error {
	.field__input-container {
		border-color: $ui-negative;
	}

	.field__icon--error,
	.field__text--error {
		display: block;
		color: $ui-negative;
	}
}
</style>

